// Pure helpers for the Agenda scheduling grid (2.3.1): per-technician colour /
// tint assignment, lane building, time-slot parsing, conflict (overlap)
// detection, and the visible day list for a given view. Adapted to run-2's
// snake_case Job / Technician model (see src/types/jobs.ts).
import { technicianColor, technicianTint } from '@/types'
import type { Job, Technician } from '@/types'
import { timeStringToMinutes } from './time-geometry'
export {
  DAY_END_HOUR,
  DEFAULT_DAY_START_HOUR,
  HOUR_ROW_PX,
} from './time-geometry'

export type AgendaView = 'day' | 'week' | 'month'

export interface TechnicianLane {
  technicianId: string
  technician: Technician
  name: string
  color: string
  tint: string
  index: number
}

export interface JobWithLane {
  job: Job
  lane: TechnicianLane
  conflict: boolean
}

export function technicianFirstName(fullName: string): string {
  return fullName.trim().split(/\s+/).filter(Boolean)[0] ?? fullName
}

export function buildLanesForTechnicians(
  technicians: Technician[]
): TechnicianLane[] {
  return technicians.map((technician, index) => ({
    technicianId: technician.id,
    technician,
    name: technicianFirstName(technician.full_name),
    color: technicianColor(index),
    tint: technicianTint(index),
    index,
  }))
}

// Builds ordered lanes (one per technician) with distinct colours + tints.
export function buildLanes(
  rows: Array<{ technician: Technician; jobs: Job[] }>
): TechnicianLane[] {
  return buildLanesForTechnicians(rows.map(row => row.technician))
}

export function technicianMapDestination(technician: Technician) {
  const cityLine = [technician.postal_code, technician.city]
    .filter(Boolean)
    .join(' ')
  const address = [technician.address, cityLine, technician.country]
    .filter(Boolean)
    .join(', ')

  return {
    address: address || technician.city || null,
    latitude: technician.latitude,
    longitude: technician.longitude,
  }
}

// Two-letter initials from a technician name (first + last word), for the
// avatar chip shown in day/week/month row & column headers.
export function laneInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  const first = parts[0][0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : ''
  return (first + last).toUpperCase()
}

// Maps technician id -> that technician's jobs (already grouped by the API).
export function jobsByTechnician(
  rows: Array<{ technician: Technician; jobs: Job[] }>
): Map<string, Job[]> {
  const map = new Map<string, Job[]>()
  for (const row of rows) map.set(row.technician.id, row.jobs)
  return map
}

export function uniqueJobsWithLane(
  lanes: TechnicianLane[],
  jobsByTech: Map<string, Job[]>
): JobWithLane[] {
  const result: JobWithLane[] = []
  const seen = new Set<string>()

  for (const lane of lanes) {
    const laneJobs = jobsByTech.get(lane.technicianId) ?? []
    const conflicts = detectConflicts(laneJobs)

    for (const job of laneJobs) {
      if (seen.has(job.id)) continue
      seen.add(job.id)
      result.push({ job, lane, conflict: conflicts.has(job.id) })
    }
  }

  return result
}

function parseHHMM(value: string | null | undefined): number | null {
  return timeStringToMinutes(value)
}

// Parses a job's start/end into start/end minutes-of-day. Falls back to a
// 60-minute block when only a start is known; null when the job is untimed.
export function parseSlotRange(
  startTime: string | null | undefined,
  endTime: string | null | undefined
): { start: number; end: number } | null {
  const start = parseHHMM(startTime)
  if (start == null) return null
  let end = parseHHMM(endTime)
  if (end == null) end = start + 60
  return { start, end: Math.max(end, start + 15) }
}

// "HH:MM-HH:MM" (or "HH:MM") label for an entry, or null when untimed.
export function slotLabel(job: Job): string | null {
  if (!job.start_time) return null
  const s = job.start_time.slice(0, 5)
  return job.end_time ? `${s}-${job.end_time.slice(0, 5)}` : s
}

export function formatSlotForCard(slot: string | null): string {
  return slot ?? '--'
}

export function formatTimeslot(job: Job, noTimeLabel: string): string {
  const slot = slotLabel(job)
  if (!slot) return noTimeLabel
  return slot.replace('-', ' - ')
}

export function sortJobsByStartTime(jobs: Job[]): Job[] {
  return [...jobs].sort((firstJob, secondJob) => {
    const firstTime = firstJob.start_time ?? ''
    const secondTime = secondJob.start_time ?? ''
    return firstTime.localeCompare(secondTime)
  })
}

// Groups jobs into clusters of mutually-reachable overlapping intervals (connected
// components). Jobs in the same cluster render side-by-side in the week/month cells.
export function clusterOverlappingJobs(jobs: Job[]): Job[][] {
  const sorted = [...jobs].sort((a, b) =>
    (a.start_time ?? '').localeCompare(b.start_time ?? '')
  )
  const clusters: Job[][] = []

  for (const job of sorted) {
    const range = parseSlotRange(job.start_time, job.end_time)
    let placed = false

    for (const cluster of clusters) {
      const overlapsCluster = cluster.some(existing => {
        const er = parseSlotRange(existing.start_time, existing.end_time)
        if (!range || !er) return false
        return range.start < er.end && er.start < range.end
      })
      if (overlapsCluster) {
        cluster.push(job)
        placed = true
        break
      }
    }

    if (!placed) clusters.push([job])
  }

  return clusters
}

// Assigns column positions to overlapping jobs so they render side-by-side.
// Returns a map of job.id → { col, totalCols } for use in DayView layout.
export function computeColumns(
  jobs: Job[]
): Map<string, { col: number; totalCols: number }> {
  const result = new Map<string, { col: number; totalCols: number }>()

  const timed = jobs
    .map(j => ({ j, r: parseSlotRange(j.start_time, j.end_time) }))
    .filter((x): x is { j: Job; r: { start: number; end: number } } =>
      Boolean(x.r)
    )
    .sort((a, b) => a.r.start - b.r.start || b.r.end - a.r.end)

  // Greedy column assignment: track the end time of the last job in each column.
  const colEnds: number[] = []
  const jobCols = new Map<string, number>()

  for (const { j, r } of timed) {
    let col = colEnds.findIndex(end => end <= r.start)
    if (col === -1) col = colEnds.length
    colEnds[col] = r.end
    jobCols.set(j.id, col)
  }

  // totalCols for a job = max column index of any overlapping job + 1.
  for (const { j, r } of timed) {
    const myCol = jobCols.get(j.id) ?? 0
    let maxCol = myCol
    for (const { j: other, r: otherR } of timed) {
      if (other.id === j.id) continue
      if (r.start < otherR.end && otherR.start < r.end) {
        maxCol = Math.max(maxCol, jobCols.get(other.id) ?? 0)
      }
    }
    result.set(j.id, { col: myCol, totalCols: maxCol + 1 })
  }

  // Untimed jobs always occupy the full lane width.
  for (const job of jobs) {
    if (!result.has(job.id)) result.set(job.id, { col: 0, totalCols: 1 })
  }

  return result
}

// Returns the set of job ids that overlap another job on the same date within a
// lane (visual conflict highlight only, never blocking - AC-2.3.1-009/-039).
export function detectConflicts(jobs: Job[]): Set<string> {
  const conflicts = new Set<string>()
  const ranges = jobs
    .map(j => ({ j, r: parseSlotRange(j.start_time, j.end_time) }))
    .filter((x): x is { j: Job; r: { start: number; end: number } } =>
      Boolean(x.r)
    )
  for (let i = 0; i < ranges.length; i++) {
    for (let k = i + 1; k < ranges.length; k++) {
      const a = ranges[i]
      const b = ranges[k]
      if (
        !a.j.scheduled_date ||
        !b.j.scheduled_date ||
        a.j.scheduled_date.slice(0, 10) !== b.j.scheduled_date.slice(0, 10)
      )
        continue
      if (a.r.start < b.r.end && b.r.start < a.r.end) {
        conflicts.add(a.j.id)
        conflicts.add(b.j.id)
      }
    }
  }
  return conflicts
}

export function toISODate(d: Date): string {
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

export function sameDay(iso: string, day: Date): boolean {
  return iso.slice(0, 10) === toISODate(day)
}

function startOfWeek(d: Date): Date {
  const copy = new Date(d)
  const dow = (copy.getDay() + 6) % 7 // Monday = 0
  copy.setDate(copy.getDate() - dow)
  copy.setHours(0, 0, 0, 0)
  return copy
}

// Inclusive list of day dates spanning the current window for the given view.
export function daysFor(anchor: Date, view: AgendaView): Date[] {
  if (view === 'day') {
    const start = new Date(anchor)
    start.setHours(0, 0, 0, 0)
    return [start]
  }
  if (view === 'week') {
    const start = startOfWeek(anchor)
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start)
      d.setDate(start.getDate() + i)
      return d
    })
  }
  // month
  const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1)
  const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0)
  return Array.from({ length: end.getDate() }, (_, i) => {
    const d = new Date(start)
    d.setDate(start.getDate() + i)
    return d
  })
}
